import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {Link} from 'shells/link/link';
import type {Level} from 'shells/status-badge/status-badge';
import {Body} from 'shells/typography/body';

import type {SnippetTestStatusEnum, DomainFeatureStatusKey} from 'nutshell-graphql-types';
import type {GetDomainFeatureStatuses_domainFeatureStatuses as DomainFeatureStatus} from 'nutshell-graphql-types';

export function getCustomDnsDomain(
    domainFeatureStatuses: Array<DomainFeatureStatus>,
    key: DomainFeatureStatusKey
): string | null | undefined {
    const domain = domainFeatureStatuses.find(
        (domainFeatureStatus) => domainFeatureStatus.key === key
    );
    if (domain && domain.fqdn) {
        return domain.fqdn;
    }

    return null;
}

export function getSnippetTestStatusMessage(
    status: SnippetTestStatusEnum,
    domain: string,
    loaderDomain: string
): React.ReactElement<any> | null {
    switch (status) {
        case 'NOT_ON_WEBSITE':
            return (
                <Body color='error'>
                    Snippet was not found on {domain}, try checking that it is installed correctly
                    and run the test again
                </Body>
            );
        case 'ON_WEBSITE_NS_DOMAIN':
            if (NutClientConfig.hasDnsRecordsForBootSnippetFF) {
                return (
                    <Body color='light'>
                        Snippet successfully detected at {domain}.{' '}
                        <Body color='warn'>
                            We recommend you {/* @ts-expect-error */}
                            <Link variant='primary' to='/setup/domains' as={RoutedLink}>
                                set a custom snippet features domain
                            </Link>{' '}
                            to prevent the snippet from being blocked by ad blockers.
                        </Body>
                    </Body>
                );
            }

            return <Body color='light'>Snippet successfully detected at {domain}</Body>;
        case 'ON_WEBSITE_CUSTOM_DOMAIN':
            return (
                <Body color='light'>
                    Snippet successfully detected at {domain}. Loading from custom domain{' '}
                    {loaderDomain}.
                </Body>
            );
    }

    return null;
}

export function getSnippetStatusText(
    hasOldSnippet?: boolean | null,
    hasNewSnippet?: boolean | null,
    hasConfiguredDnsDomain?: boolean | null,
    snippetNotWorking?: boolean | null
): string {
    if (snippetNotWorking) {
        return 'Not working';
    }

    if (!hasOldSnippet && !hasNewSnippet) {
        return 'Not detected';
    } else if (hasOldSnippet && !hasNewSnippet && !hasConfiguredDnsDomain) {
        return 'Needs upgrading';
    } else if (hasOldSnippet && !hasNewSnippet && hasConfiguredDnsDomain) {
        return 'Needs updating';
    } else if (!hasOldSnippet && hasNewSnippet && hasConfiguredDnsDomain) {
        return 'Snippet detected';
    }

    return 'Snippet needs re-embedding';
}

export function getStatusBadgeLevel(
    hasOldSnippet?: boolean | null,
    hasNewSnippet?: boolean | null,
    hasConfiguredDnsDomain?: boolean | null,
    snippetNotWorking?: boolean | null
): Level {
    if (snippetNotWorking) {
        return 'alert';
    } else if (!hasNewSnippet) {
        return 'warning';
    } else if (hasNewSnippet && !hasOldSnippet && hasConfiguredDnsDomain) {
        return 'success';
    } else {
        return 'alert';
    }
}
